import {FC, PropsWithChildren, createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Base64} from 'js-base64';
import {noop} from 'lodash';

export interface ProcessorContextValue {
  registerElement: (entityName: string, id: string, node: HTMLElement) => void;
  unregisterElement: (entityName: string, id: string) => void;
  scrollToElement: (entityName: string, id: string, scrollBehavior?: ScrollBehavior) => void;
}

const ProcessorContext = createContext<ProcessorContextValue>({
  registerElement: noop,
  unregisterElement: noop,
  scrollToElement: noop,
});

export const useScrollContext = () => useContext(ProcessorContext);

export const withScrollContext = <T extends object>(Source: FC<T>): FC<T> => (props) => {
  const scrollContext = useScrollContext();
  return <Source scrollContext={scrollContext} {...props} />;
};

export const withScrollProvider = <T extends object>(Component: FC<T>): FC<T> => (props: T) => (
  <ScrollContextProvider>
    <Component {...props} />
  </ScrollContextProvider>
);

function buildId(entityName: string, id: string): string {
  return Base64.encode(entityName) + '@' + Base64.encode(id);
}

type ScrollAnchorProps = {
  entityName: string;
  id: string;
  topOffset?: number;
};

export const ScrollAnchor: FC<ScrollAnchorProps> = ({
  topOffset = 20,
  entityName, id
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {registerElement, unregisterElement} = useScrollContext();
  useEffect(() => {
    registerElement(entityName, id, ref.current!);
    return () => unregisterElement(entityName, id);
  }, [entityName, id, registerElement, unregisterElement]);
  return <div ref={ref} style={{marginTop: `-${topOffset}px`, paddingTop: `${topOffset}px`}} />;
};

function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();

  let parent = el.parentElement;
  let scrollableParent: HTMLElement | null = null;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.getPropertyValue('overflow-y');
    const overflow = style.getPropertyValue('overflow');
    const isScrollable =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflow === 'auto' ||
      overflow === 'scroll';

    if (isScrollable) {
      scrollableParent = parent;
      break;
    }

    parent = parent.parentElement;
  }

  if (scrollableParent) {
    const parentRect = scrollableParent.getBoundingClientRect();
    return (
      rect.top >= parentRect.top &&
      rect.bottom <= parentRect.bottom
    );
  }

  return (
    rect.top >= 0 &&
    rect.bottom <= window.innerHeight
  );
}

export function ScrollContextProvider({children}: PropsWithChildren) {
  const [elementById] = useState<Map<string, HTMLElement>>(() => new Map());

  const contextValue = useMemo<ProcessorContextValue>(
    () => {
      return {
        registerElement: (entityName: string, id: string, node: HTMLElement) => {
          elementById.set(buildId(entityName, id), node);
        },
        unregisterElement: (entityName: string, id: string) => {
          elementById.delete(buildId(entityName, id));
        },
        scrollToElement: (entityName: string, id: string) => {
          const element = elementById.get(buildId(entityName, id));
          if (element && !isElementInViewport(element)) {
            element.scrollIntoView({behavior: 'auto'});
          }
        },
      };
    },
    [elementById]
  );

  return (
    <ProcessorContext.Provider value={contextValue}>{children}</ProcessorContext.Provider>
  );
}
